import { IRegionalFormatEntry } from './IRegionalFormatEntry';
import { ILanguageEntry } from './ILanguageEntry';
import { settingsManager } from '../../shared/settings';

interface IntlDisplayNamesPair {
  locale: string | undefined;
  intlDisplayNames: Intl.DisplayNames | undefined;
}

const isLocalizedNamesDisabled = settingsManager.get('isLocalizedNamesDisabled') || false;

let intlDisplayNamesPair: IntlDisplayNamesPair = {
  locale: undefined,
  intlDisplayNames: undefined,
};

export const getNeutralLanguageTag = (bcp47tag: string): string | undefined => {
  if (bcp47tag === undefined || bcp47tag.length < 2) {
    return undefined;
  }

  if (bcp47tag.indexOf('-') === -1) {
    return bcp47tag.toLowerCase();
  } else {
    return bcp47tag.toLowerCase().split('-')[0];
  }
};

export const getDisplayLanguageEntry = (
  bcp47Tag: string,
  availableDisplayLanguages: ILanguageEntry[],
  availableLanguages: ILanguageEntry[],
): ILanguageEntry => {
  const matchedDisplayLanguageEntry = getLanguageEntry(bcp47Tag, availableDisplayLanguages);
  if (matchedDisplayLanguageEntry) {
    return matchedDisplayLanguageEntry;
  }
  const matchedLanguageEntry = availableLanguages.find(
    (entry) => entry.bcp47Tag.toLowerCase() === bcp47Tag.toLowerCase(),
  );
  if (matchedLanguageEntry) {
    return matchedLanguageEntry;
  }
  return { bcp47Tag: bcp47Tag, nativeName: bcp47Tag, localizedName: bcp47Tag };
};

export const getLanguageEntry = (bcp47Tag: string, supportedLanguages: ILanguageEntry[]): ILanguageEntry => {
  if (!bcp47Tag) {
    return { bcp47Tag: '', nativeName: '', localizedName: '' };
  }

  const languageTag = bcp47Tag.toLowerCase();
  const exactMatch = supportedLanguages.find((entry) => entry.bcp47Tag.toLowerCase() === languageTag);
  if (exactMatch) {
    return exactMatch;
  }

  const neutralLanguageTag = getNeutralLanguageTag(bcp47Tag);
  const neutralMatch = supportedLanguages.find((entry) => entry.bcp47Tag.toLowerCase() === neutralLanguageTag);
  if (neutralMatch) {
    return { bcp47Tag: bcp47Tag, nativeName: neutralMatch.nativeName, localizedName: neutralMatch.localizedName };
  }

  const neutralLanguagePartMatch = supportedLanguages.filter(
    (entry) => getNeutralLanguageTag(entry.bcp47Tag) === neutralLanguageTag,
  );
  if (neutralLanguagePartMatch.length === 1) {
    return {
      bcp47Tag: bcp47Tag,
      nativeName: neutralLanguagePartMatch[0].nativeName,
      localizedName: neutralLanguagePartMatch[0].localizedName,
    };
  }
  return { bcp47Tag: bcp47Tag, nativeName: bcp47Tag, localizedName: bcp47Tag };
};

export const getNativeName = (bcp47Tag: string, supportedLanguages: ILanguageEntry[]): string => {
  return getLanguageEntry(bcp47Tag, supportedLanguages).nativeName;
};

export const getLanguageAriaLabel = (entry: ILanguageEntry): string => {
  return entry.localizedName ?? entry.nativeName;
};

export const getRegionalFormatAriaLabel = (entry: IRegionalFormatEntry): string => {
  return entry.localizedName ?? entry.displayName;
};

export const getLocalizedName = (displayLanguage: string, bcp47Tag: string): string => {
  const languageNames = getIntlDisplayNames(displayLanguage);
  const bcp47TagNoUnderscore = bcp47Tag.replace('_', '-');

  try {
    if (languageNames && languageNames.of(bcp47TagNoUnderscore)) {
      return languageNames.of(bcp47TagNoUnderscore) as string;
    } else if (fallbackLanguageNames && fallbackLanguageNames.of(bcp47TagNoUnderscore)) {
      return fallbackLanguageNames.of(bcp47TagNoUnderscore) as string;
    }
  } catch {
    // Ignored: expected error
    // Because each browser might supprot Intl.DisplayNames library differently.
  }
  return '';
};

function compareMembers(left: IRegionalFormatEntry, right: IRegionalFormatEntry): number {
  return left.displayName >= right.displayName ? 1 : -1;
}

export const isSupportedRegionalFormat = (
  bcp47tag: string,
  availableRegionalFormats: IRegionalFormatEntry[],
): boolean => {
  let ret = availableRegionalFormats.find((item) => {
    return item.locale.toLowerCase() === bcp47tag.toLowerCase();
  });
  return ret !== undefined;
};

export const filterRegionalFormats = (
  bcp47tag: string,
  availableRegionalFormats: IRegionalFormatEntry[],
): IRegionalFormatEntry[] => {
  let ret: IRegionalFormatEntry[] = [];
  const langTag = getNeutralLanguageTag(bcp47tag);

  if (langTag === undefined) {
    return availableRegionalFormats;
  }

  availableRegionalFormats.forEach((item) => {
    let entryLangTag = getNeutralLanguageTag(item.locale);
    if (langTag === entryLangTag) {
      ret.push(item);
    }
  });

  return ret.sort(compareMembers);
};

const getIntlDisplayNames = (langTag: string): Intl.DisplayNames | undefined => {
  if (isLocalizedNamesDisabled) {
    return undefined;
  }

  if (intlDisplayNamesPair.locale === langTag) {
    return intlDisplayNamesPair.intlDisplayNames;
  }

  try {
    const languageNames = new Intl.DisplayNames([langTag], { type: 'language', fallback: 'none' });
    intlDisplayNamesPair = { locale: langTag, intlDisplayNames: languageNames };
    return languageNames;
  } catch {
    // Ignored: expected error
    // Because not all browser supports Intl.DisplayNames library
  }
  return undefined;
};

const fallbackLanguageNames = getIntlDisplayNames('en-US');
